import React, { useCallback, useMemo, useContext, useEffect } from 'react'
import useGroomingVisit from '../../../../../hooks/useGroomingVisit'
import SectionFormContainer from '../../../components/SectionFormContainer'
import GroomingAppointmentWizardContext from '../GroomingAppointmentWizardContext'
import { makeStyles, CircularProgress } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import ShiftSelection from '../components/ShiftSelection'
import moment from 'moment'
import { gray, purple } from '../../../../../utils/colors'
import { FONT_WEIGHTS } from '../../../../../utils'

const amRegex = /(am)$/
const pmRegex = /(pm)$/

const useStyles = makeStyles(() => ({
	root: {
		flex: 1,
		width: '80%',
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'center',
		gap: '1rem',
		position: 'relative'
	},
	activityOverlay: {
		position: 'absolute',
		zIndex: 200,
		backgroundColor: 'rgba(255,255,255,.8)',
		width: '100%',
		height: '100%',
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center'
	},
	calendarContainer: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		'& .MuiPickersCalendarHeader-switchHeader': {
			marginBottom: '0.75rem',
			'& .MuiTypography-body1': {
				fontSize: '0.875rem',
				fontFamily: 'Nunito',
				fontWeight: FONT_WEIGHTS.ExtraBold
			},
			'& .MuiButtonBase-root.MuiIconButton-root.MuiPickersCalendarHeader-iconButton': {
				border: `1px solid ${gray[500]}`,
				width: '1.3125rem',
				height: '1.3125rem',
				margin: '0 1rem',
				'& .MuiIconButton-label': {
					'& .MuiSvgIcon-root': {
						fontSize: '1.1rem',
						color: gray[700],
						fill: gray[700]
					}
				}
			}
		},
		'& .MuiPickersBasePicker-pickerView': {
			justifyContent: 'flex-start'
		},
		'& .MuiPickersBasePicker-pickerViewLandscape': {
			padding: 0
		},
		'& .MuiPickersDay-day': {
			border: `1px solid ${gray[500]}`,
			borderRadius: '.3rem',
			color: gray[900],
			fontFamily: 'Nunito',
			fontWeight: FONT_WEIGHTS.SemiBold,
			fontSize: '0.875rem'
		},
		'& .MuiButtonBase-root.MuiPickersDay-dayDisabled': {
			border: `1px solid ${gray[500]}`,
			backgroundColor: gray[300],
			borderRadius: '.3rem',
			color: gray[700]
		},
		'& .MuiPickersCalendar-week': {
			marginBottom: '.2rem'
		},
		'& .MuiButtonBase-root.MuiPickersDay-daySelected': {
			backgroundColor: purple[100],
			borderColor: purple[700],
			borderWidth: '1.5px',
			color: purple[700]
		},
		'& .MuiButtonBase-root.MuiPickersDay-dayDisabled.MuiPickersDay-daySelected': {
			backgroundColor: gray[500],
			color: gray[900]
		},
		'& .MuiButtonBase-root.MuiIconButton-root.MuiPickersDay-day.MuiPickersDay-hidden.MuiPickersDay-dayDisabled':
			{
				opacity: 1,
				'& .MuiIconButton-label': {
					display: 'none'
				}
			},
		'& .MuiPickersCalendarHeader-daysHeader': {
			'& .MuiPickersCalendarHeader-dayLabel': {
				color: gray[900],
				fontFamily: 'Nunito',
				fontWeight: FONT_WEIGHTS.SemiBold
			}
		}
	},
	calendar: {
		backgroundColor: 'red'
	},
	timeContainer: {
		width: '50%',
		display: 'flex'
	},
	shiftOptionsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		flex: 1,
		overflow: 'scroll',
		height: '300px'
	},
	otherText: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontWeight: '600'
	}
}))

const NoShiftsView = () => {
	const classes = useStyles()
	return <div className={classes.otherText}>No shifts this month.</div>
}

const DateTimeSelector = (props) => {
	const {
		wizardContextProvider: { grooming_appointment, setWizardAttributeAndValues }
	} = useContext(GroomingAppointmentWizardContext)
	const { subtext = '', title = '' } = props
	const { getAvailability, fetchingAvailability } = useGroomingVisit()
	const classes = useStyles()

	// Leaving this here for now in case we want to display the staff name when a preferred groomer is selected
	// const staffList = useSelector((state) => state.specific.team.staffList)
	// const staffName = useMemo(() => {
	// 	if (grooming_appointment.preferred_staff_id === '' || grooming_appointment.preferred_staff_id === 'none') {
	// 		return ''
	// 	}

	// 	const staff = staffList.find((staff) => staff._id === grooming_appointment.preferred_staff_id)
	// 	if (!staff) {
	// 		return ''
	// 	}
	// 	const {
	// 		contact: { full_name }
	// 	} = staff
	// 	return full_name
	// }, [staffList, grooming_appointment])

	useEffect(() => {
		let update = false
		const { month, year, dates, preferred_staff_id } = grooming_appointment
		const [date] = dates
		const chosenMonth = moment(date).month()
		const chosenYear = moment(date).year()
		const newValues = { ...grooming_appointment }

		if (month === -1) {
			newValues.month = chosenMonth
			update = true
		}

		if (year === -1) {
			newValues.year = chosenYear
			update = true
		}

		if (preferred_staff_id !== 'none' && preferred_staff_id !== '') {
			const preferredGroomerSpecs = grooming_appointment[preferred_staff_id]
			if (preferredGroomerSpecs) {
				update = true
				const { duration, priceMax, priceMin } = preferredGroomerSpecs
				newValues.duration = duration
				newValues.priceMax = priceMax
				newValues.priceMin = priceMin
			}
		}

		if (update) {
			setWizardAttributeAndValues(newValues)
		}
	}, [])

	const handleMonthChange = useCallback(
		(date) => {
			const { month } = grooming_appointment
			const chosenMonth = moment(date).month()
			const chosenYear = moment(date).year()
			if (month > -1 && chosenMonth === month) {
				return
			}
			const newValues = {
				...grooming_appointment,
				month: chosenMonth,
				year: chosenYear,
				dates: [moment(date).format('YYYY-MM-DD')]
			}
			setWizardAttributeAndValues(newValues)
		},
		[grooming_appointment, setWizardAttributeAndValues]
	)

	const fetchAllAvailability = useCallback(async () => {
		try {
			const res = await getAvailability(grooming_appointment)
			if (res) {
				const newValues = { ...grooming_appointment, ...res }
				setWizardAttributeAndValues(newValues)
			}
		} catch (e) {
			const resetValues = {
				...grooming_appointment,
				shifts: [],
				staff: [],
				priceMin: -1,
				priceMax: -1,
				duration: -1,
				specificationsUsedInPricing: {}
			}
			setWizardAttributeAndValues(resetValues)
		}
	}, [grooming_appointment, getAvailability, setWizardAttributeAndValues])

	useEffect(() => {
		if (grooming_appointment.month > -1 && grooming_appointment.preferred_staff_id === 'none') {
			fetchAllAvailability()
		}
	}, [grooming_appointment.month])

	const handleDateChange = useCallback(
		async (value) => {
			if (value) {
				const newValues = { ...grooming_appointment }
				newValues['dates'] = [value.format('YYYY-MM-DD')]
				setWizardAttributeAndValues(newValues)
			}
		},
		[grooming_appointment, setWizardAttributeAndValues]
	)

	const handleShiftSelection = useCallback((time) => {
		if (time) {
			let shift = shiftsAvailableMap.get(time)

			let { shifts, dates, preferred_staff_id } = grooming_appointment

			if (preferred_staff_id && preferred_staff_id != 'none')
				shifts = grooming_appointment[preferred_staff_id].shifts
			const [date] = dates

			let matches = shifts.filter((s) => shift.includes(s.staff_id) && s.date === date)

			let mostTimeAvailable = matches.sort((a, b) => b.shift_minutes_available - a.shift_minutes_available)
			let assignedGroomer = mostTimeAvailable.length ? mostTimeAvailable[0] : null

			const newValues = { ...grooming_appointment, staff_id: assignedGroomer?.staff_id || null }
			newValues['dropoff_time'] = time

			setWizardAttributeAndValues(newValues)
		}
	})

	const currentSelectedDayIsOutsideOfMonthInView = useMemo(() => {
		const { dates, month } = grooming_appointment
		const [date] = dates
		const monthOfSelectedDate = moment(date).month()
		return monthOfSelectedDate !== month
	}, [grooming_appointment])

	const shiftsAvailableMap = useMemo(() => {
		const { dates, shifts, preferred_staff_id } = grooming_appointment
		let availableShifts = []
		const [date] = dates

		// If we have already fetched the staff's shifts in the previous step, use those
		if (grooming_appointment[preferred_staff_id] !== undefined) {
			availableShifts =
				grooming_appointment[preferred_staff_id].shifts.filter((shift) => shift.date === date) || []
		} else {
			availableShifts = shifts.filter((shift) => shift.date === date)
		}

		const shiftMap = new Map()

		availableShifts.forEach((shift) => {
			const { available_shift_blocks = [], staff_id } = shift
			available_shift_blocks.forEach((block) => {
				const existingBlock = shiftMap.get(block)
				if (existingBlock) {
					const newValue = [...existingBlock, staff_id]
					shiftMap.set(block, newValue)
				} else {
					shiftMap.set(block, [staff_id])
				}
			})
		})

		return shiftMap
	}, [grooming_appointment])

	const availableDays = useMemo(() => {
		const { shifts, preferred_staff_id, month } = grooming_appointment
		let daysWithShifts = []

		// If we have already fetched the staff's shifts in the previous step, use those
		if (grooming_appointment[preferred_staff_id] !== undefined) {
			daysWithShifts = grooming_appointment[preferred_staff_id].shifts
				.filter(({ date }) => {
					const monthOfDate = moment(date).month()
					if (monthOfDate === month) {
						return date
					}
				})
				.map(({ date }) => date)
		} else {
			daysWithShifts = shifts.map(({ date }) => date)
		}
		return daysWithShifts
	}, [grooming_appointment])

	const morningShiftsMap = useMemo(() => {
		const morningMap = new Map()
		shiftsAvailableMap.forEach((value, key) => {
			if (amRegex.test(key) || key === '12:00pm') {
				morningMap.set(key, value)
			}
		})
		return morningMap
	}, [shiftsAvailableMap])

	// console.log('morn...', shiftsAvailableMap)

	const eveningShiftsMap = useMemo(() => {
		const eveningMap = new Map()
		shiftsAvailableMap.forEach((value, key) => {
			if (pmRegex.test(key) && key !== '12:00pm') {
				eveningMap.set(key, value)
			}
		})
		return eveningMap
	}, [shiftsAvailableMap])

	const handleDisableDate = useCallback(
		(date) => {
			const formattedDate = date.format('YYYY-MM-DD')
			return !availableDays.includes(formattedDate)
		},
		[availableDays]
	)

	return (
		<SectionFormContainer title={title} subtext={subtext}>
			<div className={classes.root}>
				{fetchingAvailability ? (
					<div className={classes.activityOverlay}>
						<CircularProgress />
					</div>
				) : null}
				<div className={classes.calendarContainer}>
					<DatePicker
						className={classes.calendar}
						leftArrowButtonProps={{
							disableRipple: true
						}}
						rightArrowButtonProps={{
							disableRipple: true
						}}
						autoOk
						disablePast
						orientation="landscape"
						variant="static"
						openTo="date"
						value={grooming_appointment['dates'][0] || new Date()}
						onChange={handleDateChange}
						onMonthChange={handleMonthChange}
						shouldDisableDate={handleDisableDate}
						ToolbarComponent={() => null}
					/>
					{/* {staffName > '' ? <div>Dates and times for {staffName} only</div> : null} */}
				</div>
				<div className={classes.timeContainer}>
					{fetchingAvailability ? null : currentSelectedDayIsOutsideOfMonthInView ? (
						availableDays.length > 0 ? (
							<div className={classes.otherText}>Select any available day to view shifts.</div>
						) : (
							<NoShiftsView />
						)
					) : availableDays.length === 0 ? (
						<NoShiftsView />
					) : (
						<div className={classes.shiftOptionsContainer}>
							<ShiftSelection
								title="Available Morning Times"
								currentSelection={grooming_appointment.dropoff_time}
								shifts={[...morningShiftsMap.keys()]}
								onShiftSelected={handleShiftSelection}
							/>
							<ShiftSelection
								title="Available Afternoon Times"
								currentSelection={grooming_appointment.dropoff_time}
								shifts={[...eveningShiftsMap.keys()]}
								onShiftSelected={handleShiftSelection}
							/>
						</div>
					)}
				</div>
			</div>
		</SectionFormContainer>
	)
}

export default DateTimeSelector
